import classNames from 'classnames'
import React, { useCallback, useEffect, useRef, useState } from 'react'

type Props = {
  /** Max height of the scroll container as a CSS value (sets the 'max-height' property) */
  height?: string
  /** Show gradient of height `4rem` instead of `8rem` */
  small?: boolean
  gradientColor?: 'white' | 'light-gray' | 'dark-gray'
  className?: string
  children?: React.ReactNode
  noGradient?: boolean
}

const SCROLL_OFFSET = 5

export const ScrollContainer = ({
  children,
  height,
  small = false,
  gradientColor = 'white',
  className,
  noGradient,
}: Props) => {
  const [show, setShow] = useState(true)
  const ref = useRef<HTMLDivElement | null>(null)

  const updateScroll = useCallback(() => {
    if (!ref.current) {
      return
    }

    if (
      ref.current.offsetHeight + ref.current.scrollTop <
      ref.current.scrollHeight - SCROLL_OFFSET
    ) {
      setShow(true)
    } else {
      setShow(false)
    }
  }, [ref])

  useEffect(() => {
    const scroll = () => {
      updateScroll()
    }

    scroll()

    ref.current?.addEventListener('scroll', scroll)

    return () => {
      ref.current?.removeEventListener('scroll', scroll)
    }
  }, [ref])

  // Update scroll when children change
  useEffect(() => {
    if (!ref.current) {
      return
    }

    const observer = new MutationObserver(() => {
      updateScroll()
    })

    observer.observe(ref.current, {
      childList: true,
      subtree: true,
    })

    return () => {
      observer.disconnect()
    }
  }, [ref])

  return (
    <>
      <div
        className={classNames('c-scroll-container', className, {
          'c-scroll-container--show': show,
          'c-scroll-container--fluid': !height,
          'c-scroll-container--small': small,
          'c-scroll-container--no-gradient': noGradient,
          'c-scroll-container--light-gray-gradient': gradientColor === 'light-gray',
          'c-scroll-container--dark-gray-gradient': gradientColor === 'dark-gray',
        })}>
        <div
          className="c-scroll-container__content"
          style={height ? { maxHeight: height } : {}}
          ref={ref}>
          {children}
        </div>
      </div>
    </>
  )
}
